import { useState } from "react";
import { motion } from "framer-motion";
import { Settings, User, Moon, Lock } from "lucide-react";
import { useAuth } from "../Auth/AuthContext";
import userService from "../Services/userService";
import toast from "react-hot-toast";

export default function SettingsPage() {
  const { user, logout } = useAuth();

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    try {
      const dto = {
        email: user.email,           
        newPassword: passwordForm.newPassword,
      };

      const response = await userService.changePassword(dto);

      toast.success(response.message);
      setShowPasswordForm(false);
      setPasswordForm({ newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error("Failed to update password");
      console.error(err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 space-y-8"
    >
      {/* Page Header */}
      <div className="flex items-center gap-2">
        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xl md:text-2xl lg:text-3xl font-extrabold flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-gradient-x drop-shadow-sm"
        >
          <Settings className="w-6 h-6 text-pink-500 animate-bounce-slow" />
          Settings
        </motion.h2>
      </div>

      {/* Account Info */}
      <div className="bg-white p-6 rounded-xl shadow space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-700">Account</h3>
        </div>
        <p className="text-sm text-gray-600">Email: <span className="font-medium">{user?.email}</span></p>
        <p className="text-sm text-gray-600">Role: <span className="font-medium capitalize">{user?.role || 'admin'}</span></p>
        <button
          onClick={logout}
          className="mt-4 px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-xl transition"
        >
          Logout
        </button>
      </div>

      {/* Preferences */}
      <div className="bg-white p-6 rounded-xl shadow space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Moon className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-700">Preferences</h3>
        </div>
        <p className="text-sm text-gray-600">Dark Mode: (Coming Soon)</p>
        <label className="inline-flex items-center mt-2 cursor-not-allowed opacity-60">
          <input type="checkbox" className="form-checkbox text-indigo-600" disabled />
          <span className="ml-2 text-sm">Enable Dark Mode</span>
        </label>
      </div>

      {/* Security / Change Password */}
      <div className="bg-white p-6 rounded-xl shadow space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-700">Security</h3>
        </div>

        {!showPasswordForm ? (
          <button
            onClick={() => setShowPasswordForm(true)}
            className="text-sm text-indigo-600 hover:underline"
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handleSubmitPassword} className="space-y-4 mt-2">

            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <input
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                required
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-xl"
              >
                Update Password
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </motion.div>
  );
}
